// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import cardSkinApi from './cardSkin.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchCardSkinApi = createAsyncThunk(
    'itemSaga/cardSkin/fetchCardSkinApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await cardSkinApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleCardSkinPublishedStateApi = createAsyncThunk(
    'itemSaga/cardSkin/toggleCardSkinPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await cardSkinApi.update(row.id, {is_ready: !row.is_ready});
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createCardSkinApi = createAsyncThunk(
    'itemSaga/cardSkin/createCardSkinApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await cardSkinApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeCardSkinApi = createAsyncThunk(
    'itemSaga/cardSkin/storeCardSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const cardSkin = await cardSkinApi.store(values);
            alertSwal('success', 'CardSkin created successfully!');

            return cardSkin;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showCardSkinApi = createAsyncThunk(
    'itemSaga/cardSkin/showCardSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await cardSkinApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateCardSkinApi = createAsyncThunk(
    'itemSaga/cardSkin/updateCardSkinApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await cardSkinApi.update(id, values);
            alertSwal('success', 'CardSkin updated successfully!');

            const { ranks } = state.itemSaga.cardSkinMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteCardSkinApi = createAsyncThunk('itemSaga/cardSkin/deleteCardSkinApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await cardSkinApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const cardSkinSlice = createSlice({
    name: 'itemSaga/cardSkin',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedCardSkin(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCardSkinApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchCardSkinApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleCardSkinPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleCardSkinPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createCardSkinApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createCardSkinApi.rejected, () => {
                //
            });

        builder
            .addCase(storeCardSkinApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeCardSkinApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showCardSkinApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showCardSkinApi.rejected, () => {
                //
            });

        builder
            .addCase(updateCardSkinApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateCardSkinApi.rejected, () => {
                //
            });

        builder.addCase(deleteCardSkinApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedCardSkin } = cardSkinSlice.actions;
